import * as XLSX from 'xlsx';

class ExcelGenerator {
  /**
   * Converts array data to Excel worksheet with auto-sized columns
   * @param {Array} data - Array of arrays or array of objects
   * @returns {XLSX.WorkSheet} Excel worksheet
   */
  static createWorksheet(data) {
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    
    // Calculate column widths
    const columnWidths = this.calculateColumnWidths(data);
    
    // Apply column widths to worksheet
    worksheet['!cols'] = columnWidths;
    
    return worksheet;
  }

  /**
   * Calculate optimal column widths based on content
   * @param {Array} data - Array of arrays containing the data
   * @returns {Array} Array of column width specifications
   */
  static calculateColumnWidths(data) {
    const columnWidths = [];
    const DEFAULT_EXCEL_CHAR_WIDTH = 7; // Excel's default character width
    
    // Process all rows including header
    data.forEach(row => {
      row.forEach((cell, colIndex) => {
        const cellValue = String(cell);
        // Get current max width for this column
        const currentWidth = columnWidths[colIndex]?.wch || 0;
        // Excel width calculation: pixels = (characters * 7 + 5) / 7
        // We reverse this to get character count: characters = (pixels * 7 - 5) / 7
        const newWidth = Math.ceil((cellValue.length * DEFAULT_EXCEL_CHAR_WIDTH + 5) / DEFAULT_EXCEL_CHAR_WIDTH);
        
        if (!columnWidths[colIndex]) {
          columnWidths[colIndex] = { wch: newWidth };
        } else if (newWidth > currentWidth) {
          columnWidths[colIndex].wch = newWidth;
        }
      });
    });

    // Ensure minimum and maximum widths
    return columnWidths.map(col => ({
      wch: Math.min(Math.max(col.wch, 8), 50) // min 8 characters, max 50 characters
    }));
  }

  /**
   * Creates an Excel workbook with multiple sheets
   * @param {Object} sheetsData - Object with sheet names as keys and data as values
   * @returns {Blob} Excel file as Blob
   */
  static generateExcelFile(sheetsData) {
    const workbook = XLSX.utils.book_new();

    Object.entries(sheetsData).forEach(([sheetName, data]) => {
      // Create title row with larger font
      const titleRow = [sheetName];
      // Pad the title row with empty cells to match data width
      for (let i = 1; i < data[0].length; i++) {
        titleRow.push('');
      }

      // Combine title row with data
      const worksheetData = [titleRow, ...data];
      
      // Create worksheet
      const worksheet = this.createWorksheet(worksheetData);
      
      // Style the title cell
      if (!worksheet['!merges']) worksheet['!merges'] = [];
      
      // Merge cells in first row
      worksheet['!merges'].push({
        s: { r: 0, c: 0 },
        e: { r: 0, c: data[0].length - 1 }
      });

      // Add cell formatting for the title
      if (!worksheet['!rows']) worksheet['!rows'] = [];
      worksheet['!rows'][0] = { hpt: 30 }; // Set row height to 30 points
      
      // Add cell styles
      const titleCell = XLSX.utils.encode_cell({ r: 0, c: 0 });
      if (!worksheet[titleCell].s) worksheet[titleCell].s = {};
      worksheet[titleCell].s = {
        font: { bold: true, sz: 14 }, // Make title bold and larger
        alignment: { horizontal: 'center', vertical: 'center' }
      };

      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    // Generate buffer
    return XLSX.write(workbook, {
      type: 'buffer',
      bookType: 'xlsx',
      cellStyles: true // Enable cell styling
    });
  }

  /**
   * Formats table data for Excel
   * @param {Array} headers - Array of column headers
   * @param {Array} rows - Array of data rows
   * @returns {Array} Formatted data array
   */
  static formatTableData(headers, rows) {
    return [headers, ...rows];
  }

  static getColumnLetter(columnNumber) {
    let dividend = columnNumber;
    let columnName = '';
    let modulo;

    while (dividend > 0) {
      modulo = (dividend - 1) % 26;
      columnName = String.fromCharCode(65 + modulo) + columnName;
      dividend = Math.floor((dividend - modulo) / 26);
    }

    return columnName;
  }
}

export default ExcelGenerator; 